/**
 * IAN Assist — Chat Engine v2
 *
 * Deterministic intent router + parameter collector + email chunking.
 * No LLM used here — keyword/pattern matching only.
 * Returns multi-intent results so the master DAG can run them in parallel.
 *
 * Resolution order in caller:
 *   1. detectIntent → if confident match, build DAG locally
 *   2. else → caller may invoke wf.classify_intent (LLM fallback)
 */

import { LANGUAGES, COMPOSE_TONES, EMAIL_TYPES, REPLY_TONES, SUMMARY_STYLES } from '../shared/constants';

// ── Intent Definitions ──────────────────────────────────────────────────────

const INTENT_DEFS = {
  summarize: {
    keywords: ['summarize', 'summarise', 'summary', 'tldr', 'tl;dr', 'gist', 'overview', 'recap', 'digest', 'what does this email say', 'what is this about', 'break down'],
    requiredParams: [],
    optionalParams: ['summaryStyle'],
    description: 'Summarize the email',
  },
  actions: {
    keywords: ['action items', 'action points', 'tasks', 'to-do', 'todo', 'what do i need to do', 'next steps', 'deliverables', 'what actions'],
    requiredParams: [],
    optionalParams: [],
    description: 'Extract action items',
  },
  reply: {
    keywords: ['draft reply', 'draft a reply', 'draft response', 'reply to this', 'help me reply', 'write back', 'respond to this', 'reply'],
    requiredParams: [],
    optionalParams: ['replyTone'],
    description: 'Draft a reply',
  },
  translate: {
    keywords: ['translate', 'translation', 'convert to'],
    requiredParams: ['targetLanguage'],
    optionalParams: [],
    description: 'Translate the email',
  },
  rephrase: {
    keywords: ['rephrase', 'reword', 'paraphrase', 'change tone', 'soften', 'formalize', 'rewrite this'],
    requiredParams: [],
    optionalParams: ['replyTone'],
    description: 'Rephrase the email',
  },
  compose: {
    keywords: ['compose', 'write an email', 'write email', 'draft an email', 'draft email', 'new email', 'create an email', 'help me write', 'write a follow-up', 'write a'],
    requiredParams: ['composePrompt'],
    optionalParams: ['composeTone', 'composeType'],
    description: 'Compose a new email',
  },
};

// Conjunctions that signal multi-intent
// Splits on: "and then", "then", "and also", "also", "plus", "+", ";", ", then",
//            bare comma followed by a known intent verb, and "and" followed by a known intent verb.
// Intent verb prefixes: summari(ze), draft, translat(e), rephras(e), writ(e), extract,
//   list, find, reply, action, compos(e), get, give, show, what, help, paraphras(e)
const INTENT_VERB_LOOKAHEAD = '(?=summari|draft|translat|rephras|writ|extract|list|find|reply|action|compos|get|give|show|what|help|paraphras)';
const MULTI_INTENT_SPLITTERS = new RegExp(
  '\\s+(and then|then|and also|also|plus|\\+|;|,\\s+then)\\s+' +
  '|,\\s+' + INTENT_VERB_LOOKAHEAD +
  '|\\s+and\\s+' + INTENT_VERB_LOOKAHEAD,
  'i'
);

// ── Pattern compilers ──────────────────────────────────────────────────────

const LANGUAGE_PATTERNS = LANGUAGES.map((l) => ({
  key: l.key,
  // require directional preposition to avoid false positives like "english speaker"
  patterns: [
    new RegExp(`\\b(?:to|into|in)\\s+${l.label}\\b`, 'i'),
    new RegExp(`\\b${l.label}\\s+(?:translation|version)\\b`, 'i'),
  ],
}));

const TONE_PATTERNS = [...REPLY_TONES, ...COMPOSE_TONES].reduce((acc, t) => {
  if (!acc.find((x) => x.key === t.key)) {
    acc.push({
      key: t.key,
      patterns: [
        new RegExp(`\\b(?:in\\s+a\\s+)?${t.label.toLowerCase()}\\s+tone\\b`, 'i'),
        new RegExp(`\\bmake\\s+it\\s+(?:more\\s+)?${t.label.toLowerCase()}\\b`, 'i'),
        new RegExp(`\\b${t.label.toLowerCase()}\\s+style\\b`, 'i'),
      ],
    });
  }
  return acc;
}, []);

const SUMMARY_STYLE_PATTERNS = SUMMARY_STYLES.map((s) => ({
  key: s.key,
  patterns: [
    new RegExp(`\\b${s.key.replace('-', '\\s*')}\\s+summary\\b`, 'i'),
    new RegExp(`\\b${s.label.toLowerCase()}\\s+summary\\b`, 'i'),
  ],
}));

const EMAIL_TYPE_PATTERNS = EMAIL_TYPES.map((t) => ({
  key: t.key,
  patterns: [new RegExp(`\\b${t.label.toLowerCase()}\\b`, 'i')],
}));

// ── Intent Detection ────────────────────────────────────────────────────────

/**
 * Score a single intent against text. Higher = more confident.
 */
function scoreIntent(lowerText, def) {
  let score = 0;
  for (const kw of def.keywords) {
    const idx = lowerText.indexOf(kw.toLowerCase());
    if (idx >= 0) {
      // Multi-word phrases score higher
      const words = kw.split(' ').length;
      score += kw.length * (words > 1 ? 2 : 1);
    }
  }
  return score;
}

/**
 * Detect intent(s). Returns multi-intent when conjunctions found.
 *
 * @param {string} message
 * @param {Object} conversationContext
 * @returns {{
 *   intents: string[],            // 0, 1, or many
 *   primaryIntent: string|null,
 *   params: Object,
 *   missingParams: string[],
 *   followUpQuestion: Object|null,
 *   confidence: 'high'|'low'|'none',
 *   topicSwitch: boolean
 * }}
 */
export function detectIntent(message, conversationContext = {}) {
  const trimmed = (message || '').trim();
  if (!trimmed) {
    return emptyDetection();
  }
  const lower = trimmed.toLowerCase();

  // Multi-intent check
  if (MULTI_INTENT_SPLITTERS.test(lower)) {
    const parts = lower.split(MULTI_INTENT_SPLITTERS).filter((p) => p && p.length > 2);
    const subIntents = [];
    for (const part of parts) {
      const sub = detectSingleIntent(part);
      if (sub.intent && !subIntents.includes(sub.intent)) {
        subIntents.push(sub.intent);
      }
    }
    if (subIntents.length > 1) {
      const params = extractParams(lower, subIntents[0]);
      return {
        intents: subIntents,
        primaryIntent: subIntents[0],
        params,
        missingParams: [],
        followUpQuestion: null,
        confidence: 'high',
        topicSwitch: false,
      };
    }
  }

  const single = detectSingleIntent(lower);
  if (!single.intent) {
    return emptyDetection();
  }

  const def = INTENT_DEFS[single.intent];
  const params = extractParams(lower, single.intent, conversationContext);

  // Topic switch: did user clearly shift from a pending follow-up to a new intent?
  const topicSwitch = !!(conversationContext.pendingIntent &&
    conversationContext.pendingIntent !== single.intent &&
    single.score > 8);

  const missingParams = [];
  for (const p of def.requiredParams) {
    if (!params[p] && !conversationContext[p]) missingParams.push(p);
  }

  let followUpQuestion = null;
  if (missingParams.length > 0) {
    followUpQuestion = getFollowUpQuestion(missingParams[0]);
  }

  return {
    intents: [single.intent],
    primaryIntent: single.intent,
    params,
    missingParams,
    followUpQuestion,
    confidence: single.score >= 8 ? 'high' : 'low',
    topicSwitch,
  };
}

function detectSingleIntent(lowerText) {
  let bestIntent = null;
  let bestScore = 0;
  for (const [key, def] of Object.entries(INTENT_DEFS)) {
    const s = scoreIntent(lowerText, def);
    if (s > bestScore) {
      bestScore = s;
      bestIntent = key;
    }
  }
  return { intent: bestIntent, score: bestScore };
}

function emptyDetection() {
  return {
    intents: [],
    primaryIntent: null,
    params: {},
    missingParams: [],
    followUpQuestion: null,
    confidence: 'none',
    topicSwitch: false,
  };
}

// ── Parameter extraction ────────────────────────────────────────────────────

function extractParams(lowerText, intent, context = {}) {
  const params = {};

  for (const lang of LANGUAGE_PATTERNS) {
    if (lang.patterns.some((p) => p.test(lowerText))) {
      params.targetLanguage = lang.key;
      break;
    }
  }

  for (const tone of TONE_PATTERNS) {
    if (tone.patterns.some((p) => p.test(lowerText))) {
      if (intent === 'compose') params.composeTone = tone.key;
      else params.replyTone = tone.key;
      break;
    }
  }

  for (const style of SUMMARY_STYLE_PATTERNS) {
    if (style.patterns.some((p) => p.test(lowerText))) {
      params.summaryStyle = style.key;
      break;
    }
  }

  for (const etype of EMAIL_TYPE_PATTERNS) {
    if (etype.patterns.some((p) => p.test(lowerText))) {
      params.composeType = etype.key;
      break;
    }
  }

  if (intent === 'compose') {
    const cleaned = lowerText
      .replace(/\b(compose|write an email|write email|draft an email|draft email|new email|create an email|help me write|write a follow-up|write a)\b/gi, '')
      .replace(/\b(in a|with a|using a?)\s*(professional|friendly|formal|casual)\s*(tone)?\b/gi, '')
      .replace(/\babout\b/i, '')
      .trim()
      .replace(/^[:\-,\s]+|[:\-,\s]+$/g, '');
    if (cleaned.length >= 5) params.composePrompt = cleaned;
  }

  return params;
}

// ── Follow-up questions ─────────────────────────────────────────────────────

export function getFollowUpQuestion(paramName) {
  const questions = {
    targetLanguage: {
      text: 'Which language should I translate to?',
      options: LANGUAGES.map((l) => l.label),
      paramKey: 'targetLanguage',
      optionMap: Object.fromEntries(LANGUAGES.map((l) => [l.label.toLowerCase(), l.key])),
    },
    composePrompt: {
      text: 'What would you like me to write about? Please describe the email you need.',
      options: null,
      paramKey: 'composePrompt',
      optionMap: null,
      minLength: 10,
    },
    composeTone: {
      text: 'What tone would you like?',
      options: COMPOSE_TONES.map((t) => t.label),
      paramKey: 'composeTone',
      optionMap: Object.fromEntries(COMPOSE_TONES.map((t) => [t.label.toLowerCase(), t.key])),
    },
    composeType: {
      text: 'What type of email is this?',
      options: EMAIL_TYPES.map((t) => t.label),
      paramKey: 'composeType',
      optionMap: Object.fromEntries(EMAIL_TYPES.map((t) => [t.label.toLowerCase(), t.key])),
    },
    replyTone: {
      text: 'What tone should the reply use?',
      options: REPLY_TONES.map((t) => t.label),
      paramKey: 'replyTone',
      optionMap: Object.fromEntries(REPLY_TONES.map((t) => [t.label.toLowerCase(), t.key])),
    },
    summaryStyle: {
      text: 'What style of summary?',
      options: SUMMARY_STYLES.map((s) => s.label),
      paramKey: 'summaryStyle',
      optionMap: Object.fromEntries(SUMMARY_STYLES.map((s) => [s.label.toLowerCase(), s.key])),
    },
  };
  return questions[paramName] || { text: `Please provide: ${paramName}`, options: null, paramKey: paramName };
}

/**
 * Resolve a free-text answer back to a param value.
 * Validates minLength for free-text params (composePrompt) so "yes"/"no" doesn't slip through.
 */
export function resolveFollowUp(answer, followUp) {
  if (!followUp) return { resolved: false, key: '', value: '' };
  const lower = (answer || '').toLowerCase().trim();
  if (!lower) return { resolved: false, key: followUp.paramKey, value: '' };

  if (followUp.optionMap) {
    if (followUp.optionMap[lower]) {
      return { resolved: true, key: followUp.paramKey, value: followUp.optionMap[lower] };
    }
    for (const [label, key] of Object.entries(followUp.optionMap)) {
      if (lower === label || lower.includes(label) || label.includes(lower)) {
        return { resolved: true, key: followUp.paramKey, value: key };
      }
    }
    return { resolved: false, key: followUp.paramKey, value: '' };
  }

  // Free-text param
  const minLength = followUp.minLength || 3;
  if (answer.trim().length < minLength) {
    return { resolved: false, key: followUp.paramKey, value: '' };
  }
  // Reject obvious non-answers
  if (/^(yes|no|ok|okay|sure|nope|yeah)[.!?]*$/i.test(answer.trim())) {
    return { resolved: false, key: followUp.paramKey, value: '' };
  }
  return { resolved: true, key: followUp.paramKey, value: answer.trim() };
}

// ── Email Chunking ──────────────────────────────────────────────────────────

const CHUNK_SIZE = 2000;
const MAX_CHUNKS_PER_REQUEST = 3;

export function chunkEmailBody(text, chunkSize = CHUNK_SIZE) {
  if (!text) return [''];
  const cleaned = String(text).replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  if (cleaned.length <= chunkSize) return [cleaned];

  const chunks = [];
  const paragraphs = cleaned.split(/\n\n+/);
  let current = '';
  for (const para of paragraphs) {
    if (current.length + para.length + 2 > chunkSize && current.length > 0) {
      chunks.push(current.trim());
      current = para;
    } else {
      current += (current ? '\n\n' : '') + para;
    }
    // Hard split paragraphs that exceed chunk size on their own
    while (current.length > chunkSize) {
      chunks.push(current.slice(0, chunkSize));
      current = current.slice(chunkSize);
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

export function selectChunksForIntent(chunks, intent, maxChunks = MAX_CHUNKS_PER_REQUEST) {
  if (!chunks || chunks.length === 0) return '';
  if (intent === 'compose') return '';
  const selected = chunks.slice(0, maxChunks);
  const text = selected.join('\n\n---\n\n');
  if (chunks.length > maxChunks) {
    return text + `\n\n[... ${chunks.length - maxChunks} more section(s) omitted for brevity]`;
  }
  return text;
}

export function buildChunkedEmail(email, intent) {
  if (!email) return {};
  const chunks = chunkEmailBody(email.bodyText);
  const chunkedText = selectChunksForIntent(chunks, intent);
  return {
    ...email,
    bodyText: chunkedText,
    bodyHtml: '',
    _chunkInfo: {
      totalChunks: chunks.length,
      sentChunks: Math.min(chunks.length, MAX_CHUNKS_PER_REQUEST),
      chunkSize: CHUNK_SIZE,
    },
  };
}

// ── Greeting / Help / Smalltalk ─────────────────────────────────────────────

const GREETINGS_RX = /^\s*(hi+|hello+|hey+|howdy|hola|good\s+(morning|afternoon|evening)|sup|yo)[.!?\s]*$/i;
// "help" alone or help-questions, but NOT "help me <verb>" which is an intent request
const HELP_RX = /\b(what\s+can\s+you\s+do|how\s+do\s+you\s+work|capabilities|features|what\s+do\s+you\s+do)\b|^help[.!?\s]*$/i;

export function checkGreetingOrHelp(message) {
  const text = (message || '').trim();
  if (!text) return { isGreeting: false, isHelp: false, response: null };
  if (GREETINGS_RX.test(text)) {
    return {
      isGreeting: true,
      isHelp: false,
      response:
        "Hello! I'm IAN, your email assistant. I can help you with:\n\n" +
        '- **Summarize** — Get a quick summary of this email\n' +
        '- **Translate** — Translate to another language\n' +
        '- **Reply** — Draft a reply\n' +
        '- **Rephrase** — Rewrite in a different tone\n' +
        '- **Compose** — Write a new email\n' +
        '- **Action Items** — Extract tasks and next steps\n\n' +
        'You can also chain actions like "summarize and draft a reply".',
    };
  }
  if (HELP_RX.test(text)) {
    return {
      isGreeting: false,
      isHelp: true,
      response:
        "Here's what I can do:\n\n" +
        '- "Summarize this"\n' +
        '- "Translate to French"\n' +
        '- "Draft a reply in a friendly tone"\n' +
        '- "Rephrase formally"\n' +
        '- "Write a follow-up email about the proposal"\n' +
        '- "Summarize and draft a reply" (multi-intent)\n',
    };
  }
  return { isGreeting: false, isHelp: false, response: null };
}

export function getIntentDef(intentKey) {
  return INTENT_DEFS[intentKey] || null;
}

// Legacy export for tests
export const __internal__ = { INTENT_DEFS, LANGUAGE_PATTERNS, scoreIntent, extractParams, MULTI_INTENT_SPLITTERS };
